import React from 'react';
import {
  Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import InvButton from '../../../components/InvButton';
import StatusChip from '../../../components/StatusChip';
import StatBadge from '../../../components/StatBadge';
import { formatDateTime } from '../../../utils';
import {
  PRESCRIPTION_ROW_ACTION,
  resolvePrescriptionRowAction,
} from '../../../utils/prescriptionStatus';
import { IPD_COLORS, thinScrollbarSx } from '../../../theme';
import { selectedRowSx } from '../pharmacyStyles';

/**
 * Columns of the IPD order book, in the order the pharmacist reads them.
 *
 * `priority: 'low'` collapses the column below md. Those three are the ones a
 * pharmacist can reconstruct from the row they open, so on a phone the identity,
 * location, status and action survive instead of everything shrinking equally.
 */
const COLUMNS = [
  { key: 'order_no', label: 'Order No' },
  { key: 'patient', label: 'Patient' },
  { key: 'doctor', label: 'Doctor', priority: 'low' },
  { key: 'location', label: 'Location' },
  { key: 'ordered', label: 'Ordered', priority: 'low' },
  { key: 'status', label: 'Status' },
  { key: 'meds', label: 'Meds' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'actions', label: 'Actions' },
];

const LOW_PRIORITY_SX = { display: { xs: 'none', md: 'table-cell' } };

/** Same formula as WardReturnsTable — equal-width badges so labels of different length align. */
const STATUS_CHIP_MIN_WIDTH = Math.ceil('Acknowledged'.length * 8 + 24);

const ACTION_BUTTON_SX = {
  minWidth: 168,
  height: 32,
  px: 1.5,
  fontSize: 13,
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon': { mr: 0.75, '& svg': { fontSize: 18 } },
};

const headCellSx = {
  color: IPD_COLORS.textSecondary,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

function columnSx(column, base) {
  return column.priority === 'low' ? { ...base, ...LOW_PRIORITY_SX } : base;
}

function IpdPrescriptionsTable({
  rows = [],
  selectedId,
  canStart = false,
  onSelect,
  onView,
  onStartDispense,
  openDispenseByOrder,
  onOpenDispense,
}) {
  return (
    <Box sx={{ overflowX: 'auto', ...thinScrollbarSx }}>
      <Table
        size="small"
        aria-label="IPD prescriptions awaiting dispense"
        sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
      >
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column.key} sx={columnSx(column, headCellSx)}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((rx) => {
            const orderNo = rx.order_number || rx.uhid || rx.id;
            // An order with a dispense already in flight must not offer Start /
            // Continue: both reopen the start page, which creates a *second*
            // transaction (and a second controlled-auth request) instead of resuming.
            const openDispense = openDispenseByOrder?.get?.(String(rx.order_number || ''));
            const openDispenseId = openDispense?.transaction_id || openDispense?.id || '';
            const action = openDispenseId
              ? { mode: PRESCRIPTION_ROW_ACTION.NONE, label: '' }
              : resolvePrescriptionRowAction(rx.status, canStart);
            return (
              <TableRow
                key={rx.id || rx.order_number || rx.admission_id}
                onClick={() => onSelect?.(rx)}
                sx={selectedRowSx(selectedId === rx.id)}
              >
                <TableCell sx={{ fontWeight: 600, color: IPD_COLORS.primary }}>
                  {orderNo}
                </TableCell>
                <TableCell>{rx.patient_name}</TableCell>
                <TableCell sx={LOW_PRIORITY_SX}>{rx.doctor_name}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{rx.ward || '—'}</TableCell>
                <TableCell sx={{ ...LOW_PRIORITY_SX, fontSize: 13, whiteSpace: 'nowrap' }}>
                  {formatDateTime(rx.created_at)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                  <StatusChip
                    status={rx.status}
                    domain="prescription"
                    minWidth={STATUS_CHIP_MIN_WIDTH}
                  />
                </TableCell>
                <TableCell>{rx.item_count || '—'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    {rx.has_alerts && (
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.error.text }}>
                        {rx.alert_count || 'Yes'}
                      </Typography>
                    )}
                    <StatBadge show={rx.has_stat} />
                    {!rx.has_alerts && !rx.has_stat && '—'}
                  </Box>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} sx={{ verticalAlign: 'middle' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    {openDispenseId && (
                      <InvButton
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => onOpenDispense?.(openDispenseId)}
                        sx={ACTION_BUTTON_SX}
                      >
                        Open Dispense
                      </InvButton>
                    )}
                    {action.mode === PRESCRIPTION_ROW_ACTION.START
                      || action.mode === PRESCRIPTION_ROW_ACTION.CONTINUE ? (
                        <InvButton
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => onStartDispense?.(rx)}
                          sx={ACTION_BUTTON_SX}
                        >
                          {action.label}
                        </InvButton>
                      ) : null}
                    {action.mode === PRESCRIPTION_ROW_ACTION.VIEW_ONLY && (
                      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
                        {action.label}
                      </Typography>
                    )}
                    <Tooltip title="View prescription">
                      <IconButton
                        size="small"
                        sx={{ color: IPD_COLORS.primary, width: 32, height: 32 }}
                        aria-label={`View prescription ${orderNo}`}
                        onClick={() => onView?.(rx)}
                      >
                        <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default React.memo(IpdPrescriptionsTable);
